import React, { FC, useCallback, useMemo, useState } from 'react'
import {
  LoaderSpinner,
  Modal,
  ModalHeader,
} from '@lightspeed/design-system-react'
import {
  PAYMENT_TYPES,
  useFeatures,
  usePMOFFeature,
} from '@vend/business-components'
import { ApiTransaction } from '@vend/types'
import { useT } from '@transifex/react'
import { vn } from 'vend-number'
import { PERMISSIONS } from '../../../../constants'
import { selectSaleCustomer } from '../../../../state/slices/currentSale/currentSaleSlice'
import { selectTrainingModeEnabled } from '../../../../state/slices/posState/posStateSlice'
import { useAppSelector } from '../../../../state/hooks'
import {
  useUserManager,
  useVdCurrencyFilter,
} from '../../../../ng-compat/hooks'
import { isValidCustomer } from '../../../../functions/sale'
import { getLspayErrorMessage } from '../helpers/errorChecking'
import { SwiperError } from '../helpers/lspay.types'
import { useErrorMessages } from '../hooks/useErrorMessages'
import { useTrackTransactionDuration } from '../hooks/useTrackTransactionDuration'
import { LSPAYPaymentModalProps } from './LSPAYPaymentModal.types'
import { ErrorContent } from './modal-states/ErrorContent'
import { LSPAYCardNotPresentPayment } from './payment/LSPAYCardNotPresentPayment'
import { LSPAYCnpPmofPayment } from './payment/LSPAYCnpPmofPayment'

export const LSPAYCnpOnlyPaymentModal: FC<LSPAYPaymentModalProps> = ({
  amount: rawAmount,
  outletId,
  currency,
  register,
  registerSaleId,
  retailerPaymentType,
  transactionId,
  close: originalClose,
}) => {
  const [features, featuresLoading] = useFeatures(
    ['lspay_cnp_moto', 'enable_customer_saved_cards'],
    {
      lspay_cnp_moto: false,
      enable_customer_saved_cards: false,
    }
  )
  const [dismissible, setDismissible] = useState<boolean>(false)
  const [swiperError, setSwiperError] = useState<SwiperError>()
  const amount = useMemo(() => vn(rawAmount), [rawAmount])
  const vdCurrencyFilter = useVdCurrencyFilter()
  const getErrorMessage = useErrorMessages()
  const t = useT()
  const trackEnd = useTrackTransactionDuration({
    registerId: register.id,
    transactionId,
    paymentTypeId: PAYMENT_TYPES.LSPAY,
    isRefund: false,
    amount: amount.toNumber(),
  })
  const saleCustomer = useAppSelector(selectSaleCustomer)
  const customerId = isValidCustomer(saleCustomer) ? saleCustomer.id : undefined
  const { isEnabled: lspayPMOFSettingEnabled } = usePMOFFeature()
  const lspayPMOFuserPermissionGranted = useUserManager().hasPermission(
    PERMISSIONS.LSPAY_PMOF_ADD_EDIT_USE
  )
  const trainingMode = useAppSelector(selectTrainingModeEnabled)

  const closeModal = useCallback(
    (val?: Promise<ApiTransaction>) => {
      trackEnd(!!val)
      originalClose(val)
    },
    [originalClose, trackEnd]
  )

  const closeWithoutTerminalSideEffects = useCallback(() => {
    closeModal()
  }, [closeModal])
  const ignoreTerminalPaymentDisable = useCallback(() => {}, [])

  if (featuresLoading) {
    return (
      <Modal size="medium" focusTrapActive={true}>
        <div
          className="vd-flex vd-flex--justify-center"
          style={{ height: '100%' }}
        >
          <LoaderSpinner />
        </div>
      </Modal>
    )
  }

  if (swiperError) {
    const lspayError = getLspayErrorMessage({
      err: swiperError,
      vdCurrencyFilter,
      retailerPaymentTypeName: retailerPaymentType.name,
      t,
      getErrorMessage,
    })

    return (
      <Modal
        size="medium"
        focusTrapActive={true}
        dismissable={true}
        onClose={closeWithoutTerminalSideEffects}
        className="lspay-payment-modal"
      >
        <ErrorContent
          retry={() => setSwiperError(undefined)}
          close={closeWithoutTerminalSideEffects}
          lspayError={lspayError}
        />
      </Modal>
    )
  }

  const isSettingEnabledByMerchantAndFlagEnabled =
    features.enable_customer_saved_cards && lspayPMOFSettingEnabled
  const isCurrentUserSessionEligibleForPMOF =
    lspayPMOFuserPermissionGranted && !trainingMode
  const useProcessingPMOF =
    isSettingEnabledByMerchantAndFlagEnabled &&
    isCurrentUserSessionEligibleForPMOF &&
    !!customerId

  return (
    <Modal
      size="medium"
      focusTrapActive={true}
      dismissable={dismissible}
      onClose={closeWithoutTerminalSideEffects}
      className="lspay-payment-modal"
    >
      <ModalHeader>
        {t('Pay {amount} with {retailerPaymentTypeName}', {
          amount: vdCurrencyFilter(amount.toNumber()),
          retailerPaymentTypeName: retailerPaymentType.name,
          _context: 'Payment modal header',
        })}
      </ModalHeader>
      {useProcessingPMOF ? (
        <LSPAYCnpPmofPayment
          amount={amount}
          customerId={customerId}
          outletId={outletId}
          register={register}
          registerSaleId={registerSaleId}
          registerSalePaymentId={transactionId}
          retailerPaymentTypeId={retailerPaymentType.id}
          setSwiperError={setSwiperError}
          setDisableTerminalPayment={ignoreTerminalPaymentDisable}
          onError={closeWithoutTerminalSideEffects}
          onSuccess={closeModal}
          onClose={closeWithoutTerminalSideEffects}
          setDismissible={setDismissible}
          disableTerminalMonitoring={true}
        />
      ) : (
        <LSPAYCardNotPresentPayment
          setDisableTerminalPayment={ignoreTerminalPaymentDisable}
          setDismissible={setDismissible}
          close={closeModal}
          onCancelPayment={closeWithoutTerminalSideEffects}
          outletId={outletId}
          amount={amount}
          currency={currency}
          register={register}
          retailerPaymentTypeId={retailerPaymentType.id}
          registerSaleId={registerSaleId}
          transactionId={transactionId}
          setSwiperError={setSwiperError}
          hasMoto={features.lspay_cnp_moto}
        />
      )}
    </Modal>
  )
}
